/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  EpkModuleApiService,
  getFilteredData,
} from '~packages/epk-front-shared/api-client';
import type { Filters, Params } from '~packages/epk-front-shared/api-client';
import { Mode, RequestMethod } from '~packages/json-api/client';
import replacePlaceholdersCustom, {
  PathParts,
} from '~packages/json-api/client/helpers/__base/replacePlaceholdersCustom';

import getConstants from '@/constants';
import { useLphsStore } from '@/store';

const { isModeDevelopment } = getConstants();

interface MemberInfo {
  period: string;
  type: string | number;
  general_account: string;
  head_id: string;
}

const constructorParams = {
  className: 'LphsApiService',
  mode: isModeDevelopment ? Mode.Production : Mode.Production,
  needInit: isModeDevelopment,
};

export default class LphsApiService extends EpkModuleApiService {
  _lphStore = useLphsStore();

  constructor() {
    super(constructorParams);
    this.bigIntCb = (res: any) => {
      return res;
    };
  }

  async approve(id: string | number) {
    const parts = { id };
    return await this._sendRequest(
      replacePlaceholdersCustom('/{id}/approve', parts as unknown as PathParts),
      null,
      { method: RequestMethod.PUT },
      { method: 'approve' }
    );
  }

  async approveRPC(id: string | number) {
    const parts = { id };
    return await this._sendRequest(
      replacePlaceholdersCustom('/{id}/approve', parts as unknown as PathParts),
      null,
      { method: RequestMethod.PUT },
      { method: 'approveRPC', options: { headers: { 'rpc-execute': true } } }
    );
  }

  async getAllCards(params?: any) {
    return await super.fetchAll(params);
  }

  async getCard(lph: string) {
    return await super.fetchAll({ lph });
  }

  async getFilteredData(filters: Filters, params: Params) {
    return await getFilteredData(this, filters, params);
  }

  async createLphs(data: any) {
    return await this._sendRequest(
      this.pathUpdate + 'v2',
      null,
      { method: this.requestMethodAdd, body: JSON.stringify(data) },
      { method: 'createLphs' }
    );
  }

  async updateLphs(data: any, path: string) {
    const enrichmentData = { ...data, ...this._lphStore.getReEnrichmentData };
    return await super.update(enrichmentData, path);
  }

  async updateLphsInformation(data: any, path: string) {
    return await this._sendRequest(
      this.pathUpdate + 'history/dimensions/' + path,
      null,
      { method: this.requestMethodUpdate, body: JSON.stringify(data) },
      { method: 'updateLphsInformation' }
    );
  }

  async brokenLph(code: string) {
    return await this._sendRequest(
      this.pathUpdate + code + '/broken ',
      null,
      { method: this.requestMethodUpdate },
      { method: 'brokenLph' }
    );
  }
  async unbrokenLph(code: string) {
    return await this._sendRequest(
      this.pathUpdate + code + '/unbroken ',
      null,
      { method: this.requestMethodUpdate },
      { method: 'unbrokenLph' }
    );
  }

  async updateLphsByPointCode(data: any, code: string) {
    return await this._sendRequest(
      this.pathUpdate + 'all/point/' + code,
      null,
      { method: this.requestMethodUpdate, body: JSON.stringify(data) },
      { method: 'updateLphsByPointCode' }
    );
  }

  async getLphsId(id: string) {
    return await super.fetch(id);
  }

  async getByPointCode(code: string | number, params?: any) {
    return await this._sendRequest(
      this.pathFetch + 'all/point/' + code,
      params,
      { method: this.requestMethodFetch },
      { method: 'getByPointCode' }
    );
  }

  async getMembersData(data: MemberInfo, params?: any) {
    return await this._sendRequest(
      this.pathFetch +
        'all/dimensions/' +
        data.period +
        '/' +
        data.type +
        '/' +
        data.general_account +
        '/' +
        data.head_id,
      params,
      { method: this.requestMethodFetch },
      { method: 'getMembersData' }
    );
  }

  async deleteAccountByPointCode(code: string, params?: any) {
    return await this._sendRequest(
      this.pathDelete + 'dot/point/' + code,
      params,
      { method: this.requestMethodDelete },
      { method: 'deleteAccountByPointCode' }
    );
  }
  async changeAccountInformation(data: MemberInfo, params?: any) {
    return await this._sendRequest(
      this.pathDelete +
        'can-edit/' +
        data.period +
        '/' +
        data.type +
        '/' +
        data.general_account +
        '/' +
        data.head_id,
      params,
      { method: this.requestMethodFetchAll },
      { method: 'changeAccountInformation' }
    );
  }
  async refreshLphsByPointCode(code: string) {
    return await this._sendRequest(
      this.pathUpdate + code + '/refresh',
      null,
      { method: this.requestMethodUpdate },
      { method: 'refreshLphsByPointCode' }
    );
  }
  async getLphsByPointCode(code: string, params: any) {
    return await this._sendRequest(
      this.pathDelete + 'dot/point/' + code,
      params,
      { method: this.requestMethodFetch },
      { method: 'getLphsByPointCode' }
    );
  }
  async deleteByCode(code: string) {
    return await this._sendRequest(
      `/${code}`,
      undefined,
      { method: RequestMethod.DELETE },
      { method: 'delete' }
    );
  }
}
